import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Screen from '../../components/common/Screen';
import NavBar from '../../components/common/NavBar';
import Icon from '../../components/common/Icon';
import { apiRequest, teamPhoto } from '../../utils';

import '../user/PublicProfile.css';
import './PublicTeam.css';
const userFallback = require('./../../assets/images/fallback/user.png');

const Layout = ({ children }) => (
  <Screen>
    <div className="top">{children}</div>
    <div className="bottom">
      <NavBar />
    </div>
  </Screen>
);

const Page = () => {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const { teamId } = useParams();

  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('newest');

  useEffect(() => {
    if (!teamId) { setStatus('notfound'); return; }
    let cancelled = false;
    setStatus('loading');
    apiRequest('GET', `/public-team/${teamId}/cheers`)
      .then((res) => { if (!cancelled) { setData(res); setStatus('ok'); } })
      .catch(() => { if (!cancelled) setStatus('notfound'); });
    return () => { cancelled = true; };
  }, [teamId]);

  const team = data?.team;
  const cheers = data?.cheers || [];
  const totalVotes = data?.total_votes ?? cheers.length;
  const teamPicture = teamPhoto(team?.picture, team?.company_picture);
  const heroIsCompanyLogo = !team?.picture && Boolean(team?.company_picture);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = cheers;
    if (q) {
      list = list.filter(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.message || '').toLowerCase().includes(q)
      );
    }
    const sorted = [...list];
    if (sort === 'newest') {
      sorted.sort((a, b) => new Date(b.post_date) - new Date(a.post_date));
    } else if (sort === 'oldest') {
      sorted.sort((a, b) => new Date(a.post_date) - new Date(b.post_date));
    } else if (sort === 'name') {
      sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }
    return sorted;
  }, [cheers, query, sort]);

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });
    } catch {
      return '';
    }
  };

  if (status === 'loading') {
    return (
      <Layout>
        <div className="pp-page">
          <div className="pp-skeleton pp-skel-hero" />
          <div className="pp-skeleton pp-skel-row" />
          <div className="pp-skeleton pp-skel-row" />
        </div>
      </Layout>
    );
  }

  if (status === 'notfound' || !team) {
    return (
      <Layout>
        <div className="pp-page">
          <div className="pp-empty">
            <div className="pp-empty-icon"><Icon name="info" /></div>
            <div className="pp-empty-title">{t('publicTeamNotFound', { defaultValue: 'Team not found' })}</div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pp-page">
        <div className="pt-cheers-topbar">
          <button
            type="button"
            className="pt-cheers-back"
            onClick={() => navigate(`/public-team/${teamId}`)}
            aria-label={t('backToTeam', { defaultValue: 'Back' })}
          >
            <Icon name="angle-left" />
          </button>
          <h1 className="pt-cheers-title">{t('allVotes')}</h1>
        </div>

        {/* HERO — votes count */}
        <section className="pt-cheers-hero">
          <div className="pt-cheers-hero-bg" style={{ backgroundImage: `url(${teamPicture})` }} />
          <div className="pt-cheers-hero-inner">
            <div className="pp-avatar-ring">
              <img
                src={teamPicture}
                alt=""
                className={`pp-avatar ${heroIsCompanyLogo ? 'pt-avatar-contain' : ''}`}
              />
            </div>
            <div className="pt-cheers-eyebrow">{t('cheers')}</div>
            <div className="pt-cheers-count">{totalVotes}</div>
            {team.name && <div className="pt-cheers-team">{team.name}</div>}
          </div>
        </section>

        {/* SEARCH + SORT */}
        {cheers.length > 0 && (
          <section className="pp-section">
            <div className="pt-cheers-controls">
              <div className="pt-cheers-search">
                <Icon name="search" />
                <input
                  type="text"
                  placeholder={t('searchVotes')}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
              <div className="pt-cheers-sort">
                {[
                  { key: 'newest', label: t('newest', { defaultValue: 'Newest' }) },
                  { key: 'oldest', label: t('oldest', { defaultValue: 'Oldest' }) },
                  { key: 'name',   label: t('byName', { defaultValue: 'A → Z' }) },
                ].map(opt => (
                  <button
                    key={opt.key}
                    type="button"
                    className={`pt-cheers-sort-btn ${sort === opt.key ? 'is-active' : ''}`}
                    onClick={() => setSort(opt.key)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* LIST */}
        {filtered.length === 0 ? (
          <div className="pp-empty">
            <div className="pp-empty-icon"><Icon name="party-horn" pack="sr" /></div>
            <div className="pp-empty-title">
              {query ? t('noResultsFound', { defaultValue: 'No results' }) : t('noVotesYet')}
            </div>
          </div>
        ) : (
          <div className="pt-cheer-list pt-cheer-list--full">
            {filtered.map((c) => (
              <div key={c.id} className="pt-cheer">
                <img src={c.photo_url || userFallback} alt="" className="pt-cheer-avatar" />
                <div className="pt-cheer-body">
                  <div className="pt-cheer-row">
                    <div className="pt-cheer-name">{c.name}</div>
                    {c.post_date && <span className="pt-cheer-date">{formatDate(c.post_date)}</span>}
                  </div>
                  {c.message && <p className="pt-cheer-message">{c.message}</p>}
                </div>
              </div>
            ))}
          </div>
        )}

        {filtered.length > 0 && (
          <div className="pt-cheers-footnote">
            <Icon name="check" /> {filtered.length} / {totalVotes}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Page;
